/**
 * 统一的错误对象。
 *
 * 契约 §3.2 的裁定是「出网边界只有点分式一种形态」，所以界面**按 `code` 分支**，
 * 不按 HTTP 状态码 —— `AUTH.CODE_TOO_FREQUENT` 与 `COMMON.RATE_LIMITED` 都是 429，
 * 处置却相反。这个类保证无论错在哪一层，界面拿到的形状都一样。
 */
import type { FieldError } from '@cione/contracts'

/**
 * 客户端侧产生的码。
 *
 * ⚠ 这些**不是契约码** —— 契约码只由服务端产生。用 `CLIENT.` 前缀是为了保证
 *   永远不会与出网码相撞(出网码的域白名单里没有 CLIENT)，同时形态一致，
 *   界面可以走同一条「按 code 出文案」的路。文案键在 M1 的 i18n 表里补，
 *   补之前它们会落 `error.unknown` 兜底 —— 那也是对的，只是不够具体。
 */
export const CLIENT_NETWORK = 'CLIENT.NETWORK'
export const CLIENT_TIMEOUT = 'CLIENT.TIMEOUT'
export const CLIENT_ABORTED = 'CLIENT.ABORTED'
/** 服务端回了成功壳，但 `data` 与契约 schema 对不上 —— 契约漂移，必须显眼。 */
export const CLIENT_CONTRACT_DRIFT = 'CLIENT.CONTRACT_DRIFT'

export interface ApiErrorInit {
  code: string
  message: string
  traceId: string
  retryable: boolean
  fieldErrors?: readonly FieldError[] | undefined
  httpStatus?: number | undefined
  cause?: unknown
}

export class ApiError extends Error {
  /** 点分式错误码。界面按它分支。 */
  readonly code: string
  /** ⚠ 一律优先取自 `x-trace-id` **响应头** —— SSE 流与网关 502 没有响应体。 */
  readonly traceId: string
  /** 服务端给的判据(契约 §2.1)。⚠ 它是提示位，退多久退几次由前端定。 */
  readonly retryable: boolean
  /** 非空 ⇒ 表单能改的问题(字段级标红)；为空 ⇒ 按 code 出整体文案。 */
  readonly fieldErrors: readonly FieldError[]
  /** 仅供排查与日志。**不要拿它分支**。 */
  readonly httpStatus: number | undefined

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause })
    this.name = 'ApiError'
    this.code = init.code
    this.traceId = init.traceId
    this.retryable = init.retryable
    this.fieldErrors = init.fieldErrors ?? []
    this.httpStatus = init.httpStatus
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}
